import React, { useState, useEffect, useRef } from 'react';
import { preprocessMarkdown } from '../../../common/helpers';
import { marked } from 'marked';
import Loader from "../../../common/loader";
import DOMPurify from 'dompurify';

const BroadbeanModal = ({ description, onClose, onSave, isSaving, error }) => {
  const [editableHtml, setEditableHtml] = useState('');
  const [isEditable, setIsEditable] = useState(false);
  const contentRef = useRef(null);
  useEffect(() => {
    if (description) {
      const rawHTML = marked.parse(preprocessMarkdown(description));
      const styledHTML = rawHTML.replace(/<(h[1-6])([^>]*)>/g, '<$1$2 class="mph-primary text-sm">');
      const sanitizedHTML = DOMPurify.sanitize(styledHTML, { USE_PROFILES: { html: true } });
      setEditableHtml(sanitizedHTML);
    }
  }, [description]);

  // When description changes, reset edit mode
  useEffect(() => {
    setIsEditable(false);
  }, [description]);

  const handleSave = () => {
    if (contentRef.current) {
      const updatedHtml = contentRef.current.innerHTML;
      onSave(updatedHtml);
      setIsEditable(false);
    }
  };

  const handleEditClick = () => {
    setIsEditable(true);
    // Optionally focus the content area
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.focus();
      }
    }, 0);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-3/4 h-3/4 flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 pt-6 pb-2 z-10 rounded-t-lg flex items-center">
          <h2 className="text-lg mph-primary font-semibold">Job Description for Broadbean</h2>
          {!isEditable && (
            <button
              className="px-3 py-1 text-xs rounded-full mph-light mph-primary font-semibold transition flex items-center gap-2"
              onClick={handleEditClick}
              disabled={isEditable || isSaving}
              type="button"
              aria-label="Edit Description"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="Edit / Edit_Pencil_Line_02">
                  <path id="Vector" d="M4 20.0001H20M4 20.0001V16.0001L14.8686 5.13146L14.8704 5.12976C15.2652 4.73488 15.463 4.53709 15.691 4.46301C15.8919 4.39775 16.1082 4.39775 16.3091 4.46301C16.5369 4.53704 16.7345 4.7346 17.1288 5.12892L18.8686 6.86872C19.2646 7.26474 19.4627 7.46284 19.5369 7.69117C19.6022 7.89201 19.6021 8.10835 19.5369 8.3092C19.4628 8.53736 19.265 8.73516 18.8695 9.13061L18.8686 9.13146L8 20.0001L4 20.0001Z" stroke="#004645" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                </g>
              </svg>
            </button>
          )}
        </div>

        <div className={`flex-1 px-6 py-2 relative ${description ? 'overflow-y-auto' : ''}`}>
          <div className="mb-2 h-full">
            {!editableHtml ? (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-20">
                <Loader message="Loading description" />
              </div>
            ) : (
              <div
                ref={contentRef}
                contentEditable={isEditable}
                suppressContentEditableWarning
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    marked.parse(preprocessMarkdown(description))
                      .replace(/<(h[1-6])([^>]*)>/g, '<$1$2 class="mph-primary text-sm">')
                  )
                }}
                className={`h-full prose prose-green max-w-none text-xs mph-jobdesc-markdown`}
                style={{
                  outline: 'none',
                  border: 'none',
                  cursor: 'text'
                }}
                tabIndex={0}
              />
            )}
            {error && <div className="text-red-500 text-xs mt-2">{error}</div>}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white px-6 pb-3 pt-3 z-10 rounded-b-lg">
          <div className="flex justify-end gap-2">
            <button
              className="px-3 text-xs py-1 rounded-full bg-gray-200 font-semibold text-gray-333 hover:bg-gray-300"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="px-3 py-1 text-xs rounded-full profiler-bg text-white font-semibold"
              onClick={handleSave}
              disabled={isSaving || !description || !isEditable}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadbeanModal;
